import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Publication } from '@pf/shared';
import { usePublicationsInfinite, usePublicationCategories } from '../hooks/usePublications';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function PublicationFavicon({ url }: { url: string | null; name: string }) {
  const [error, setError] = useState(false);
  if (url && !error) {
    return (
      <img
        src={url}
        alt=""
        className="w-10 h-10 rounded-md object-contain flex-shrink-0"
        onError={() => setError(true)}
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    </div>
  );
}

function CategoryFilter({
  selected,
  onChange,
}: {
  selected: string | undefined;
  onChange: (category: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = usePublicationCategories();
  const categories = data?.categories ?? [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (selected) {
    return (
      <button
        onClick={() => onChange(undefined)}
        className="flex items-center gap-1.5 h-8 px-3 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
      >
        <span className="max-w-[140px] truncate">{selected}</span>
        <span className="text-blue-400 hover:text-blue-600 text-sm leading-none">×</span>
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 h-8 px-3 text-xs border border-gray-300 rounded-full hover:border-gray-400 bg-white text-gray-700 transition-colors"
      >
        Category
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-52 max-h-64 overflow-y-auto">
          {categories.length === 0 ? (
            <div className="py-3 text-center text-xs text-gray-400">No categories available</div>
          ) : (
            categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  onChange(cat);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors text-gray-700"
              >
                {cat}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

type SortOption = { value: 'name' | 'pageRank' | 'reportersCount'; label: string; defaultOrder: 'asc' | 'desc' };

const SORT_OPTIONS: SortOption[] = [
  { value: 'name', label: 'Name', defaultOrder: 'asc' },
  { value: 'pageRank', label: 'Domain Authority', defaultOrder: 'desc' },
  { value: 'reportersCount', label: 'Reporter Count', defaultOrder: 'desc' },
];

const MIN_PAGE_RANK_OPTIONS = [
  { value: undefined, label: 'Any authority' },
  { value: 3, label: '3+ (established)' },
  { value: 5, label: '5+ (popular)' },
  { value: 7, label: '7+ (major)' },
];

function SortControl({
  sortBy,
  sortOrder,
  onSortChange,
}: {
  sortBy: 'name' | 'pageRank' | 'reportersCount';
  sortOrder: 'asc' | 'desc';
  onSortChange: (by: 'name' | 'pageRank' | 'reportersCount', order: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const current = SORT_OPTIONS.find((o) => o.value === sortBy);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 h-8 px-3 text-xs border border-gray-300 rounded-full hover:border-gray-400 bg-white text-gray-700 transition-colors"
      >
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
        Sort: {current?.label}
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-48">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                const newOrder = opt.value === sortBy
                  ? (sortOrder === 'asc' ? 'desc' : 'asc')
                  : opt.defaultOrder;
                onSortChange(opt.value, newOrder);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors text-gray-700 flex items-center justify-between"
            >
              <span>{opt.label}</span>
              {sortBy === opt.value && (
                <svg
                  className={`w-3 h-3 text-blue-600 ${sortOrder === 'asc' ? '' : 'rotate-180'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PageRankBadge({ rank }: { rank: number | null }) {
  if (rank === null || rank === undefined) return null;
  const rounded = Math.round(rank * 10) / 10;

  let colorClass = 'bg-gray-100 text-gray-500';
  if (rank >= 7) colorClass = 'bg-blue-50 text-blue-700';
  else if (rank >= 5) colorClass = 'bg-emerald-50 text-emerald-700';
  else if (rank >= 3) colorClass = 'bg-yellow-50 text-yellow-700';

  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}
      title={`Domain authority: ${rounded}/10`}
    >
      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      {rounded}
    </span>
  );
}

function PublicationCard({ publication }: { publication: Publication }) {
  const navigate = useNavigate();
  const topCategories = publication.categories.slice(0, 3);

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer transition-all"
      onClick={() => navigate(`/publications/${publication.id}`)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <PublicationFavicon url={publication.faviconUrl} name={publication.name} />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate">
              {publication.name}
            </h3>
            {publication.url && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">{publication.url}</p>
            )}
          </div>
          {publication.pageRank !== null && (
            <div className="flex-shrink-0">
              <PageRankBadge rank={publication.pageRank} />
            </div>
          )}
        </div>

        {publication.description && (
          <p className="mt-2 text-xs text-gray-600 line-clamp-2 leading-relaxed">
            {publication.description}
          </p>
        )}

        {topCategories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {topCategories.map((cat) => (
              <span
                key={cat}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700"
              >
                {cat}
              </span>
            ))}
            {publication.categories.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                +{publication.categories.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {publication.reportersCount.toLocaleString()}{' '}
            {publication.reportersCount === 1 ? 'reporter' : 'reporters'}
          </span>
          {publication.audienceSizeEstimate && (
            <span className="text-xs text-gray-400">{publication.audienceSizeEstimate} readers</span>
          )}
          <svg
            className="w-4 h-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function PublicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') ?? '';
  const category = searchParams.get('category') ?? undefined;
  const sortBy = (searchParams.get('sortBy') ?? 'name') as 'name' | 'pageRank' | 'reportersCount';
  const sortOrder = (searchParams.get('sortOrder') ?? 'asc') as 'asc' | 'desc';
  const minPageRankStr = searchParams.get('minPageRank');
  const minPageRank = minPageRankStr ? Number(minPageRankStr) : undefined;

  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebounce(searchInput, 400);

  useEffect(() => {
    const current = searchParams.get('search') ?? '';
    if (debouncedSearch === current) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('search', debouncedSearch);
        else next.delete('search');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch]);

  function updateFilter(key: string, value: string | undefined) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value !== undefined) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  function handleSortChange(by: 'name' | 'pageRank' | 'reportersCount', order: 'asc' | 'desc') {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sortBy', by);
        next.set('sortOrder', order);
        return next;
      },
      { replace: true },
    );
  }

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePublicationsInfinite({
    search: search || undefined,
    category,
    sortBy,
    sortOrder,
    minPageRank,
  });

  const allPublications = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  const activeFilterCount = [!!category, minPageRank !== undefined].filter(Boolean).length;

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900">Publications</h1>
        {!isLoading && (
          <p className="mt-1 text-sm text-gray-500">
            {total > 0
              ? `${total.toLocaleString()} publication${total !== 1 ? 's' : ''}${search ? ` matching "${search}"` : ''}`
              : search || activeFilterCount > 0
                ? 'No publications match your filters'
                : 'No publications found'}
          </p>
        )}
      </div>

      <div className="mb-4">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Search publications by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <CategoryFilter
          selected={category}
          onChange={(cat) => updateFilter('category', cat)}
        />

        {/* Min page rank filter */}
        <select
          value={minPageRank ?? ''}
          onChange={(e) => updateFilter('minPageRank', e.target.value || undefined)}
          className="h-8 px-2.5 text-xs border border-gray-300 rounded-full bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
        >
          {MIN_PAGE_RANK_OPTIONS.map((opt) => (
            <option key={opt.value ?? ''} value={opt.value ?? ''}>
              {opt.label}
            </option>
          ))}
        </select>

        <SortControl
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />

        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('category');
                next.delete('minPageRank');
                return next;
              }, { replace: true });
            }}
            className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-full hover:border-gray-400 transition-colors"
          >
            Clear filters ({activeFilterCount})
          </button>
        )}
      </div>

      {isLoading && (
        <div className="py-16 text-center text-sm text-gray-500">Loading publications...</div>
      )}
      {isError && (
        <div className="py-16 text-center text-sm text-red-600">Failed to load publications.</div>
      )}

      {!isLoading && !isError && allPublications.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500">
            {search || activeFilterCount > 0
              ? 'No publications match your search.'
              : 'No publications found.'}
          </p>
        </div>
      )}

      {!isLoading && allPublications.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {allPublications.map((pub) => (
              <PublicationCard key={pub.id} publication={pub} />
            ))}
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-xs text-gray-400">
              Showing {allPublications.length.toLocaleString()} of {total.toLocaleString()} publications
            </p>
            {hasNextPage && (
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-gray-700 transition-colors"
              >
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
